
import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Calendar, ArrowRight, Plus } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Carousel, CarouselContent, CarouselItem, CarouselDots } from '@/components/ui/carousel';
import { ActivePollCardEnhanced } from './ActivePollCardEnhanced';
import { GroupSelectionDialog } from '@/components/polls/GroupSelectionDialog';
import { motion } from 'framer-motion';

interface ActivePollsWidgetProps {
  polls: any[];
  loading: boolean;
  onRespond: (pollId: string, response: 'available' | 'unavailable') => Promise<boolean>;
  onWithdraw: (pollId: string) => Promise<boolean>;
  currentUserId?: string;
}

export const ActivePollsWidget: React.FC<ActivePollsWidgetProps> = ({
  polls,
  loading,
  onRespond,
  onWithdraw,
  currentUserId
}) => {
  const navigate = useNavigate();
  const [showGroupSelection, setShowGroupSelection] = useState(false);

  if (loading) {
    return (
      <Card className="border-border">
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center">
            <Calendar className="h-4 w-4 mr-2 text-padel-green-light" />
            Active Polls
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {[1, 2].map((i) => (
              <div key={i} className="animate-pulse bg-muted rounded-lg h-32"></div>
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  if (polls.length === 0) {
    return null;
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.2 }}
    >
      <Card className="border-border">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-base flex items-center">
              <Calendar className="h-4 w-4 mr-2 text-padel-green-light" />
              Active Polls
            </CardTitle>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowGroupSelection(true)}
                className="text-padel-green-dark hover:text-padel-green-dark/90 text-xs flex items-center gap-1"
              >
                <Plus className="h-3 w-3" />
                Create Poll
              </Button>
              {polls.length > 2 && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => navigate('/groups')}
                  className="text-padel-green-dark hover:text-padel-green-dark/90 text-xs"
                >
                  View All <ArrowRight className="h-3 w-3 ml-1" />
                </Button>
              )}
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <Carousel className="w-full" opts={{ align: "start", loop: false }}>
            <CarouselContent className="-ml-2 md:-ml-4">
              {polls.map((poll, index) => (
                <CarouselItem key={poll.id} className="pl-2 md:pl-4 basis-full md:basis-1/2">
                  <ActivePollCardEnhanced
                    poll={poll}
                    onRespond={onRespond}
                    onWithdraw={onWithdraw}
                    loading={loading}
                    index={index}
                    currentUserId={currentUserId}
                  />
                </CarouselItem>
              ))}
            </CarouselContent>
            <CarouselDots />
          </Carousel>
        </CardContent>
      </Card>
      
      <GroupSelectionDialog
        open={showGroupSelection}
        onOpenChange={setShowGroupSelection}
      />
    </motion.div>
  );
};
